import React, { useState, useEffect } from 'react';
import { todoAPI } from '../services/api';

function Todo() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', due_date: '', priority: 'medium', category: ''
  });

  useEffect(() => { fetchItems(); }, []);

  const fetchItems = async () => {
    try {
      const response = await todoAPI.getAll();
      setItems(response.data);
    } catch (error) { console.error('Error fetching todo items:', error); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) { await todoAPI.update(editingId, formData); }
      else { await todoAPI.create(formData); }
      fetchItems(); resetForm();
    } catch (error) { console.error('Error saving todo:', error); alert('Failed to save todo'); }
  };

  const handleToggle = async (id) => {
    try { await todoAPI.toggle(id); fetchItems(); }
    catch (error) { console.error('Error toggling todo:', error); }
  };

  const handleEdit = (item) => {
    setFormData({
      title: item.title, description: item.description || '',
      due_date: item.due_date ? item.due_date.split('T')[0] : '',
      priority: item.priority, category: item.category || ''
    });
    setEditingId(item.id); setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Delete this task?')) {
      try { await todoAPI.delete(id); fetchItems(); }
      catch (error) { console.error('Error deleting todo:', error); }
    }
  };

  const handleClearCompleted = async () => {
    if (window.confirm('Clear all completed tasks?')) {
      try { await todoAPI.clearCompleted(); fetchItems(); }
      catch (error) { console.error('Error clearing completed tasks:', error); }
    }
  };

  const resetForm = () => {
    setFormData({ title: '', description: '', due_date: '', priority: 'medium', category: '' });
    setEditingId(null); setShowForm(false);
  };

  const getPriorityColor = (priority) => {
    const colors = { high: 'var(--accent-coral)', medium: 'var(--accent-amber)', low: 'var(--accent-teal)' };
    return colors[priority] || 'var(--text-muted)';
  };

  const getPriorityBadge = (priority) => {
    const badges = { high: 'badge-danger', medium: 'badge-warning', low: 'badge-success' };
    return `badge ${badges[priority]}`;
  };

  const activeItems = items.filter(item => !item.is_completed);
  const completedItems = items.filter(item => item.is_completed);

  if (loading) return <div className="loading"><div className="spinner"></div></div>;

  return (
    <div className="container fade-in">
      <div className="page-header">
        <div>
          <h2>To-Do List</h2>
          <p>Family tasks and reminders</p>
        </div>
        <div className="flex gap-1">
          {completedItems.length > 0 && (
            <button onClick={handleClearCompleted} className="btn btn-secondary">
              Clear Done
            </button>
          )}
          <button onClick={() => setShowForm(!showForm)} className="btn btn-primary">
            {showForm ? 'Cancel' : '+ Add Task'}
          </button>
        </div>
      </div>

      <div className="card">
        {showForm && (
          <div className="form-section">
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="form-label">Task *</label>
                <input type="text" className="form-input" value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})} required
                  placeholder="What needs to be done?" />
              </div>
              <div className="form-group">
                <label className="form-label">Description</label>
                <textarea className="form-textarea" value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  placeholder="Optional details..." />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Due Date</label>
                  <input type="date" className="form-input" value={formData.due_date}
                    onChange={(e) => setFormData({...formData, due_date: e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">Priority</label>
                  <select className="form-select" value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}>
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Category</label>
                  <input type="text" className="form-input" placeholder="e.g. Work, Personal, Home"
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})} />
                </div>
                <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', paddingBottom: '16px' }}>
                  <div className="flex gap-1">
                    <button type="submit" className="btn btn-success">
                      {editingId ? 'Update' : 'Add'}
                    </button>
                    <button type="button" onClick={resetForm} className="btn btn-secondary">Cancel</button>
                  </div>
                </div>
              </div>
            </form>
          </div>
        )}

        {items.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">✓</div>
            <p className="empty-state-text">No tasks yet</p>
            <button onClick={() => setShowForm(true)} className="btn btn-primary">
              Add Your First Task
            </button>
          </div>
        ) : (
          <>
            {/* Active Items */}
            {activeItems.length > 0 && (
              <div>
                <div style={{
                  padding: '12px 24px 6px', fontSize: '12px', fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em'
                }}>
                  To Do ({activeItems.length})
                </div>
                {activeItems.map((item) => (
                  <div key={item.id} className="list-item">
                    <div className="checkbox-wrapper">
                      <input type="checkbox" className="checkbox" checked={item.is_completed}
                        onChange={() => handleToggle(item.id)} />
                    </div>
                    <div style={{
                      width: '8px', height: '8px', borderRadius: '50%', flexShrink: 0,
                      background: getPriorityColor(item.priority)
                    }} />
                    <div className="list-item-content">
                      <div className="list-item-title">
                        {item.title}
                        <span className={getPriorityBadge(item.priority)}>{item.priority}</span>
                      </div>
                      <div className="list-item-subtitle">
                        {item.due_date && `Due: ${new Date(item.due_date).toLocaleDateString()}`}
                        {item.category && ` · ${item.category}`}
                      </div>
                    </div>
                    <div className="list-item-actions">
                      <button onClick={() => handleEdit(item)} className="btn btn-secondary btn-sm">Edit</button>
                      <button onClick={() => handleDelete(item.id)} className="btn btn-danger btn-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Completed Items */}
            {completedItems.length > 0 && (
              <div>
                <div style={{
                  padding: '16px 24px 6px', fontSize: '12px', fontWeight: 700,
                  color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em'
                }}>
                  Completed ({completedItems.length})
                </div>
                {completedItems.map((item) => (
                  <div key={item.id} className="list-item completed">
                    <div className="checkbox-wrapper">
                      <input type="checkbox" className="checkbox" checked={item.is_completed}
                        onChange={() => handleToggle(item.id)} />
                    </div>
                    <div className="list-item-content">
                      <div className="list-item-title">{item.title}</div>
                      {item.completed_date && (
                        <div className="list-item-subtitle">
                          Completed: {new Date(item.completed_date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="list-item-actions">
                      <button onClick={() => handleDelete(item.id)} className="btn btn-danger btn-sm">Delete</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default Todo;
